import { useParams, Link } from 'react-router-dom'
import { useAdminData } from '../hooks/useAdminData'

interface CountEntry {
  key: string
  count: number
}

interface AgentRunSummary {
  slug: string
  runs: number
  ok: number
  failures: number
  avg_duration_ms: number | null
}

interface UserDetailQuestion {
  id: string
  question: string
  question_type: string
  context?: string | null
  answered_at?: string | null
  dismissed: boolean
  times_asked: number
  created_at: string
}

interface UserDetailConceptEntry {
  concept: string
  coverage_score: number
  memory_count: number
}

interface UserDetailDailyActivity {
  date: string
  messages: number
}

interface UserDetailConnector {
  provider: string
  is_connected: boolean
  provider_email?: string | null
  scopes: string[]
  last_sync_at?: string | null
  error_message?: string | null
}

interface UserDetail {
  id: string
  email: string
  display_name: string
  avatar_url?: string | null
  timezone: string
  plan: string
  plan_status: string
  account_status: string
  status_reason?: string | null
  onboarded_at?: string | null
  created_at: string
  last_active_at?: string | null
  mfa_enrolled_at?: string | null
  monthly_budget_usd?: number | null
  is_founder: boolean
  onboarding: {
    target: number
    answered: number
    pending: number
    dismissed: number
    recent_questions: UserDetailQuestion[]
  }
  brain: {
    memories_total: number
    memories_active: number
    memories_by_source_type: CountEntry[]
    memories_by_epistemic_status: CountEntry[]
    memories_by_maturity: CountEntry[]
    entities_total: number
    entities_by_type: CountEntry[]
    knowledge_map_concepts: number
    knowledge_map_avg_coverage: number
    top_concepts: UserDetailConceptEntry[]
    identity_summary?: string | null
    identity_thinking_style?: string | null
    identity_last_analyzed_at?: string | null
    vault_files_count: number
  }
  activity: {
    messages_7d: number
    messages_30d: number
    messages_total: number
    conversations_total: number
    daily_last_30d: UserDetailDailyActivity[]
    agent_runs_30d: AgentRunSummary[]
  }
  connectors: UserDetailConnector[]
  costs: {
    today_usd: number
    month_usd: number
    lifetime_usd: number
    avg_daily_usd: number
  }
  proposals: {
    calendar_pending: number
    calendar_confirmed: number
    calendar_rejected: number
    calendar_failed: number
    email_label_pending: number
    email_label_applied: number
    email_label_rejected: number
    email_label_failed: number
  }
}

const cardStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.8)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '20px 24px',
}

const sectionTitle: React.CSSProperties = {
  fontFamily: 'var(--serif)',
  fontSize: '18px',
  fontWeight: 600,
  color: 'var(--ink)',
  margin: '0 0 14px 0',
}

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--ink-muted)',
  marginBottom: '4px',
}

const valueStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  color: 'var(--ink)',
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-PT')
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value ?? '—'}</div>
    </div>
  )
}

function CountBar({ entries, total }: { entries: CountEntry[]; total?: number }) {
  const max = Math.max(1, total ?? entries.reduce((a, b) => a + b.count, 0))
  if (entries.length === 0) {
    return <div style={{ color: 'var(--ink-muted)', fontSize: '12px', fontFamily: 'var(--sans)' }}>—</div>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontFamily: 'var(--sans)' }}>
      {entries.map((e) => {
        const pct = (e.count / max) * 100
        return (
          <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <div style={{ width: '100px', color: 'var(--ink-light)' }}>{e.key}</div>
            <div style={{ flex: 1, height: '6px', background: 'var(--cream-dark)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)' }} />
            </div>
            <div style={{ width: '40px', textAlign: 'right', color: 'var(--ink)', fontWeight: 500 }}>{e.count}</div>
          </div>
        )
      })}
    </div>
  )
}

function DailySparkline({ daily }: { daily: UserDetailDailyActivity[] }) {
  if (daily.length === 0) {
    return <div style={{ color: 'var(--ink-muted)', fontSize: '12px' }}>Sem actividade nos últimos 30 dias</div>
  }
  const max = Math.max(1, ...daily.map((d) => d.messages))
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '60px' }}>
      {daily.map((d) => {
        const h = (d.messages / max) * 100
        return (
          <div
            key={d.date}
            title={`${d.date}: ${d.messages} mensagens`}
            style={{
              flex: 1,
              height: `${Math.max(2, h)}%`,
              background: d.messages > 0 ? 'var(--accent)' : 'var(--cream-dark)',
              borderRadius: '2px',
              minWidth: '4px',
            }}
          />
        )
      })}
    </div>
  )
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useAdminData<UserDetail>(`/users/${id}`, [id])

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--ink-muted)', fontFamily: 'var(--sans)' }}>
        A carregar...
      </div>
    )
  }
  if (error || !data) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#dc2626', fontFamily: 'var(--sans)' }}>
        {error || 'Utilizador não encontrado'}
        <div style={{ marginTop: '16px' }}>
          <Link to="/admin/utilizadores" style={{ color: 'var(--ink-light)' }}>← Voltar à lista</Link>
        </div>
      </div>
    )
  }

  const onboardingPct =
    data.onboarding.target > 0 ? Math.round((data.onboarding.answered / data.onboarding.target) * 100) : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Link
            to="/admin/utilizadores"
            style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--ink-muted)', textDecoration: 'none' }}
          >
            ← Utilizadores
          </Link>
          <h1
            style={{
              fontFamily: 'var(--serif)',
              fontSize: '28px',
              fontWeight: 600,
              color: 'var(--ink)',
              margin: '8px 0 0 0',
            }}
          >
            {data.display_name || data.email}
          </h1>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--ink-muted)', marginTop: '2px' }}>
            {data.email}
          </div>
        </div>
      </div>

      {/* Identidade */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Identidade</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' }}>
          <Field label="Plano" value={data.plan} />
          <Field label="Plan status" value={data.plan_status} />
          <Field label="Conta" value={data.account_status} />
          <Field label="Founder" value={data.is_founder ? 'sim' : 'não'} />
          <Field label="Fuso horário" value={data.timezone} />
          <Field label="MFA" value={data.mfa_enrolled_at ? formatDate(data.mfa_enrolled_at) : 'não activo'} />
          <Field label="Criado" value={formatDate(data.created_at)} />
          <Field label="Onboarded" value={data.onboarded_at ? formatDate(data.onboarded_at) : 'não'} />
          <Field label="Último activo" value={formatDateTime(data.last_active_at)} />
          <Field
            label="Orçamento"
            value={data.monthly_budget_usd != null ? `$${data.monthly_budget_usd.toFixed(0)}/mês` : 'ilimitado'}
          />
          {data.status_reason && <Field label="Razão" value={data.status_reason} />}
        </div>
      </div>

      {/* Onboarding */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Onboarding</h2>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '12px',
                color: 'var(--ink-muted)',
                marginBottom: '6px',
              }}
            >
              {data.onboarding.answered} / {data.onboarding.target} perguntas respondidas ({onboardingPct}%)
            </div>
            <div style={{ height: '8px', background: 'var(--cream-dark)', borderRadius: '4px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, onboardingPct)}%`,
                  background: 'var(--accent)',
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '14px', fontFamily: 'var(--sans)', fontSize: '13px' }}>
            <div>
              <span style={{ color: 'var(--ink-muted)' }}>Pendentes: </span>
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{data.onboarding.pending}</span>
            </div>
            <div>
              <span style={{ color: 'var(--ink-muted)' }}>Dispensadas: </span>
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{data.onboarding.dismissed}</span>
            </div>
          </div>
        </div>
        {data.onboarding.recent_questions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontFamily: 'var(--sans)' }}>
            {data.onboarding.recent_questions.map((q) => (
              <div
                key={q.id}
                style={{
                  padding: '10px 12px',
                  background: 'var(--cream-dark)',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {q.question_type}
                    {q.times_asked > 0 && ` · asked ${q.times_asked}×`}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--ink-muted)' }}>
                    {q.answered_at ? `resp. ${formatDate(q.answered_at)}` : q.dismissed ? 'dispensada' : 'pendente'}
                  </span>
                </div>
                <div style={{ color: 'var(--ink)' }}>{q.question}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cérebro */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Cérebro</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <Field label="Memórias activas" value={data.brain.memories_active} />
          <Field label="Memórias total" value={data.brain.memories_total} />
          <Field label="Entidades" value={data.brain.entities_total} />
          <Field label="Conceitos (mapa)" value={data.brain.knowledge_map_concepts} />
          <Field label="Cobertura média" value={`${(data.brain.knowledge_map_avg_coverage * 100).toFixed(0)}%`} />
          <Field label="Vault files" value={data.brain.vault_files_count} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
          <div>
            <div style={labelStyle}>Por tipo de fonte</div>
            <CountBar entries={data.brain.memories_by_source_type} />
          </div>
          <div>
            <div style={labelStyle}>Por epistemic status</div>
            <CountBar entries={data.brain.memories_by_epistemic_status} />
          </div>
          <div>
            <div style={labelStyle}>Por maturity</div>
            <CountBar entries={data.brain.memories_by_maturity} />
          </div>
          <div>
            <div style={labelStyle}>Entidades por tipo</div>
            <CountBar entries={data.brain.entities_by_type} />
          </div>
        </div>

        {data.brain.top_concepts.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div style={labelStyle}>Top conceitos (por cobertura)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontFamily: 'var(--sans)', fontSize: '12px' }}>
              {data.brain.top_concepts.map((c) => (
                <div key={c.concept} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1, color: 'var(--ink)' }}>{c.concept}</div>
                  <div style={{ color: 'var(--ink-muted)', fontSize: '11px' }}>{c.memory_count} mem</div>
                  <div style={{ width: '80px', height: '4px', background: 'var(--cream-dark)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.min(100, c.coverage_score * 100)}%`,
                        height: '100%',
                        background: 'var(--accent)',
                      }}
                    />
                  </div>
                  <div style={{ width: '40px', textAlign: 'right', color: 'var(--ink-light)' }}>
                    {(c.coverage_score * 100).toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(data.brain.identity_summary || data.brain.identity_thinking_style) && (
          <div style={{ marginTop: '20px', padding: '14px', background: 'var(--cream-dark)', borderRadius: '8px' }}>
            <div style={labelStyle}>Identidade inferida</div>
            {data.brain.identity_summary && (
              <div style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--ink)', marginBottom: '8px' }}>
                {data.brain.identity_summary}
              </div>
            )}
            {data.brain.identity_thinking_style && (
              <div style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--ink-light)' }}>
                <strong>Estilo:</strong> {data.brain.identity_thinking_style}
              </div>
            )}
            {data.brain.identity_last_analyzed_at && (
              <div style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: 'var(--ink-muted)', marginTop: '6px' }}>
                Analisado {formatDateTime(data.brain.identity_last_analyzed_at)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actividade */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Actividade</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <Field label="Msgs 7d" value={data.activity.messages_7d} />
          <Field label="Msgs 30d" value={data.activity.messages_30d} />
          <Field label="Msgs total" value={data.activity.messages_total} />
          <Field label="Conversas" value={data.activity.conversations_total} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={labelStyle}>Últimos 30 dias</div>
          <DailySparkline daily={data.activity.daily_last_30d} />
        </div>
        {data.activity.agent_runs_30d.length > 0 && (
          <div>
            <div style={labelStyle}>Agentes (últimos 30d)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--ink-muted)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 0' }}>Agente</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Runs</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>OK</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Falhas</th>
                  <th style={{ textAlign: 'right', padding: '6px 0' }}>Dur. média</th>
                </tr>
              </thead>
              <tbody>
                {data.activity.agent_runs_30d.map((a) => (
                  <tr key={a.slug} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 0', color: 'var(--ink)' }}>{a.slug}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{a.runs}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--accent)' }}>{a.ok}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', color: a.failures > 0 ? '#dc2626' : 'var(--ink-muted)' }}>
                      {a.failures}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 0', color: 'var(--ink-light)' }}>
                      {a.avg_duration_ms != null ? `${(a.avg_duration_ms / 1000).toFixed(1)}s` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Conectores */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Conectores</h2>
        {data.connectors.length === 0 ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: '13px', fontFamily: 'var(--sans)' }}>Sem conectores.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
            {data.connectors.map((c) => (
              <div
                key={c.provider}
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  background: c.is_connected ? 'rgba(220, 252, 231, 0.3)' : 'var(--cream-dark)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ fontFamily: 'var(--sans)', fontWeight: 600, color: 'var(--ink)', textTransform: 'capitalize' }}>
                    {c.provider}
                  </div>
                  <span
                    style={{
                      fontSize: '10px',
                      padding: '2px 8px',
                      borderRadius: '9999px',
                      background: c.is_connected ? '#dcfce7' : '#f3f4f6',
                      color: c.is_connected ? '#166534' : '#4b5563',
                      fontFamily: 'var(--sans)',
                      fontWeight: 600,
                    }}
                  >
                    {c.is_connected ? 'ligado' : 'desligado'}
                  </span>
                </div>
                {c.provider_email && (
                  <div style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--ink-light)' }}>{c.provider_email}</div>
                )}
                {c.scopes.length > 0 && (
                  <div style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: 'var(--ink-muted)', marginTop: '4px' }}>
                    {c.scopes.length} scope{c.scopes.length === 1 ? '' : 's'}
                  </div>
                )}
                {c.last_sync_at && (
                  <div style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: 'var(--ink-muted)', marginTop: '4px' }}>
                    Sync: {formatDateTime(c.last_sync_at)}
                  </div>
                )}
                {c.error_message && (
                  <div style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>
                    {c.error_message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custos */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Custos</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '16px' }}>
          <Field label="Hoje" value={`$${data.costs.today_usd.toFixed(4)}`} />
          <Field label="Este mês" value={`$${data.costs.month_usd.toFixed(2)}`} />
          <Field label="Lifetime" value={`$${data.costs.lifetime_usd.toFixed(2)}`} />
          <Field label="Média diária (30d)" value={`$${data.costs.avg_daily_usd.toFixed(4)}`} />
        </div>
      </div>

      {/* Propostas */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Propostas</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div style={labelStyle}>Calendário</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontFamily: 'var(--sans)', fontSize: '13px' }}>
              <ProposalCell label="Pendente" value={data.proposals.calendar_pending} fg="#d97706" />
              <ProposalCell label="Confirmado" value={data.proposals.calendar_confirmed} fg="var(--accent)" />
              <ProposalCell label="Rejeitado" value={data.proposals.calendar_rejected} fg="var(--ink-muted)" />
              <ProposalCell label="Falhou" value={data.proposals.calendar_failed} fg="#dc2626" />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Labels de email</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontFamily: 'var(--sans)', fontSize: '13px' }}>
              <ProposalCell label="Pendente" value={data.proposals.email_label_pending} fg="#d97706" />
              <ProposalCell label="Aplicado" value={data.proposals.email_label_applied} fg="var(--accent)" />
              <ProposalCell label="Rejeitado" value={data.proposals.email_label_rejected} fg="var(--ink-muted)" />
              <ProposalCell label="Falhou" value={data.proposals.email_label_failed} fg="#dc2626" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProposalCell({ label, value, fg }: { label: string; value: number; fg: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px', background: 'var(--cream-dark)', borderRadius: '6px' }}>
      <div style={{ fontSize: '18px', fontWeight: 600, color: fg, fontFamily: 'var(--serif)' }}>{value}</div>
      <div style={{ fontSize: '10px', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
    </div>
  )
}
